import { verbatim } from '@myre/common-i18n';
import { isNotNil } from '@myre/common-primitives';
import { type UiI18n, type UiLibI18n } from '@myre/domain-i18n-wordings';
import { DotsLoaderComponent } from '@myre/ui-primitive-components';
import {
  CheckboxComponent,
  DropdownComponent,
  DropdownTemplate,
  MyuiPeriodPicker,
  SwitchComponent,
  TextboxComponent,
  type MyuiPeriodPickerValue,
} from '@myre/ui-primitive-forms';
import { ArrowsDatePickerComponent } from '@myre/ui-primitive-tiles';

import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostBinding,
  inject,
  input,
  linkedSignal,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormsModule,
  NG_VALUE_ACCESSOR,
  type ControlValueAccessor,
} from '@angular/forms';
import Fuse from 'fuse.js';
import { castArray, compact, isEqual, isNil, uniq, uniqBy } from 'lodash-es';
import {
  distinctUntilChanged,
  of,
  Subject,
  throttleTime,
  type Observable,
} from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { WORDINGS } from '../../../../myre-wordings-loader';

import * as FilterHelper from '../helpers/filter.helper';
import {
  type Filter,
  type FilterGroupSelection,
  type FilterOption,
  type FilterValue,
  type FilterValueAtom,
  type PeriodPickerFilter,
  type SelectFilter,
} from '../interfaces/filters/base.filter';
import { type DateFilter } from '../interfaces/filters/date.filter';
import { type SwitchFilter } from '../interfaces/filters/switch.filter';

import { TagLabelPipe } from './tag-label/tag-label.pipe';

/** `false` is a selection like any other — a flag filter set to "no" narrows the list. */
function hasSelection(value: FilterValue | null): boolean {
  return Array.isArray(value) ? value.length > 0 : isNotNil(value);
}

@Component({
  selector: 'myre-filter-item',
  templateUrl: './filter-item.component.html',
  styleUrl: './filter-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ArrowsDatePickerComponent,
    CheckboxComponent,
    DotsLoaderComponent,
    DropdownComponent,
    DropdownTemplate,
    FormsModule,
    MyuiPeriodPicker,
    SwitchComponent,
    TagLabelPipe,
    TextboxComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: FilterItemComponent,
      multi: true,
    },
  ],
})
export class FilterItemComponent<
  Key extends string,
> implements ControlValueAccessor {
  public readonly filter = input.required<Filter<Key>>();

  /** Same filter key the analytics events carry — stable, i18n-proof E2E selector. */
  @HostBinding('attr.data-testid')
  public get testId(): string {
    return `filter.${this.filter().key}`;
  }

  protected _periodPicker = viewChild<MyuiPeriodPicker>('periodPicker');
  protected _dropdown = viewChild<DropdownComponent<FilterOption>>('dropdown');

  protected readonly _wordings = inject<UiI18n & UiLibI18n>(WORDINGS);

  protected readonly searchFilter = signal<string>('');

  /**
   * Selected group values per group index, used to recompute the union expansion.
   * Linked to the resolved selection so clearing the filter (cross or programmatic
   * reset) also drops the group sub-selections — otherwise their tags reappear
   * stale in the dropdown header the next time it is opened.
   */
  protected readonly _selectedGroupsByIndex = linkedSignal<
    FilterValue,
    Record<number, FilterValue>
  >({
    source: () => this._selectedOptions(),
    computation: (selectedOptions, previous) => {
      const isCleared =
        isNil(selectedOptions) ||
        (Array.isArray(selectedOptions) && selectedOptions.length === 0);

      return isCleared ? {} : (previous?.value ?? {});
    },
  });

  protected readonly _groups = computed<FilterGroupSelection<Key>[]>(
    () => this._basicFilter().groupSelections ?? [],
  );

  /** Category cell currently expanded into the aside column, or `null` when none. */
  protected readonly _activeGroupIndex = signal<number | null>(null);

  protected readonly _activeGroup = computed<FilterGroupSelection<Key> | null>(
    () => {
      const index = this._activeGroupIndex();

      return isNil(index) ? null : (this._groups()[index] ?? null);
    },
  );

  protected readonly _asyncSearchResults = signal<readonly FilterOption[]>([]);

  /** Labels resolved on demand for preselected values absent from the preloaded options. */
  protected readonly _resolvedSelectedOptions = signal<readonly FilterOption[]>(
    [],
  );

  protected readonly _isResolving = signal(false);

  /** Flipped by the first opening: the option listing is fetched then, not at mount. */
  private readonly _hasBeenOpened = signal(false);

  private readonly _hasListedOptions = signal(false);

  private readonly _isSearching = signal(false);

  /** A dropdown showing the previous term's options would read as the answer to the new one. */
  protected readonly _isLoadingOptions = computed<boolean>(
    () =>
      typeof this._basicFilter().searchable === 'function' &&
      (!this._hasListedOptions() || this._isSearching()),
  );

  private readonly _searchSubject = new Subject<{
    query: string;
    search: (query: string) => Observable<readonly FilterOption[]>;
  }>();

  private readonly _resolveSubject = new Subject<readonly FilterValue[]>();

  /**
   * List of options to display in the dropdown.
   * If the search filter is not empty, the options are filtered by the search filter but the selected items are always displayed on top.
   */
  protected readonly filteringOptions = computed<FilterOption[]>(() => {
    if (
      typeof this._basicFilter().searchable !== 'function' &&
      !this.searchFilter()?.length
    ) {
      return this._displayOptions();
    }

    const selectedOptions = castArray(this._selectedOptions());
    const currentSelectedOptions = this._displayOptions().filter(option => {
      const optionValues = castArray(option.value);

      return selectedOptions.some(selectedOption =>
        optionValues.includes(selectedOption),
      );
    });

    const searchResults =
      typeof this._basicFilter().searchable === 'function'
        ? this._asyncSearchResults()
        : this._fuse()
            .search(this.searchFilter())
            .map(({ item }) => item);

    // A listing still on its way must not read as an empty one — the loader says it.
    const noResults =
      searchResults.length === 0 && !this._isLoadingOptions()
        ? [
            {
              label: this._wordings._library.dropdown.noResults(),
              value: '',
              disabled: true,
            },
          ]
        : [];

    return uniqBy(
      compact([...currentSelectedOptions, ...searchResults, ...noResults]),
      option => option.value,
    );
  });

  protected readonly _editMode = signal(true);

  protected _selectedOptions = signal<FilterValue>([]);

  protected _basicFilter = computed(() => {
    return this.filter() as SelectFilter<Key>;
  });

  protected readonly _displayOptions = computed<FilterOption[]>(() => {
    const baseOptions = this._basicFilter().options ?? [];
    const resolvedOptions = this._resolvedSelectedOptions();

    if (!resolvedOptions.length) {
      return baseOptions;
    }

    return uniqBy([...baseOptions, ...resolvedOptions], option => option.value);
  });

  protected readonly _displayFilter = computed<
    Filter<Key> | PeriodPickerFilter
  >(() => {
    const filter = this.filter();

    if (!FilterHelper.isFilter(filter)) {
      return filter;
    }

    return { ...filter, options: this._displayOptions() };
  });

  // Reads `options` only (never the resolved set) to avoid a resolution feedback loop.
  private readonly _unresolvedSelectedValues = computed<FilterValue[]>(() => {
    const filter = this._basicFilter();

    if (typeof filter.resolveSelectedOptions !== 'function') {
      return [];
    }

    const selectedValues = castArray(this._selectedOptions() ?? []).filter(
      isNotNil,
    );

    if (!selectedValues.length) {
      return [];
    }

    const knownValues = new Set(
      (filter.options ?? []).map(option => option.value),
    );

    return selectedValues.filter(value => !knownValues.has(value));
  });

  protected _periodPickerFilter = computed(() => {
    return this.filter() as PeriodPickerFilter;
  });

  protected _dateFilter = computed(() => {
    return this.filter() as DateFilter<Key>;
  });

  protected _switchFilter = computed(() => {
    return this.filter() as SwitchFilter<Key>;
  });

  protected readonly _dateValue = computed<Date | null>(() => {
    const value = this._selectedOptions();

    return value instanceof Date ? value : null;
  });

  protected readonly _switchValue = computed<boolean>(
    () => this._selectedOptions() === true,
  );

  protected _periodPickerFilterSelectedValue = linkedSignal({
    source: () => {
      const option = this._selectedOptions();

      if (Array.isArray(option)) {
        return option.at(0) as unknown as MyuiPeriodPickerValue;
      }

      return option as unknown as MyuiPeriodPickerValue;
    },
    computation: value => value,
  });

  private readonly _fuse = computed(() => {
    // Only create Fuse instance for local search (not for function-based search)
    if (typeof this._basicFilter().searchable === 'function') {
      return null;
    }

    return new Fuse(this._basicFilter().options ?? [], {
      keys: ['label'],
      // threshold 0 means : perfect match
      // threshold 1 means : every item is a match
      threshold: this._basicFilter().searchable === 'fuzzy' ? 0.3 : 0,
    });
  });

  constructor() {
    this._searchSubject
      .pipe(
        // The source is part of the identity, not just the query: switching view swaps
        // it while the query stays empty, and the options it serves have to be fetched
        // again — deduplicating on the query alone would keep the previous view's.
        distinctUntilChanged(
          (a, b) => a.query === b.query && a.search === b.search,
        ),
        throttleTime(300, undefined, { leading: true, trailing: true }),
        tap(() => this._isSearching.set(true)),
        switchMap(({ query, search }) =>
          // A failure must not tear the pipe down: the dropdown would stay on its
          // loader and the next keystrokes would search nothing.
          search(query).pipe(catchError(() => of<readonly FilterOption[]>([]))),
        ),
        takeUntilDestroyed(),
      )
      .subscribe(results => {
        this._hasListedOptions.set(true);
        this._isSearching.set(false);
        this._asyncSearchResults.set(results);
      });

    // Gated on the first opening: a filter the user never unfolds must query nothing.
    effect(() => {
      if (!this._hasBeenOpened()) {
        return;
      }

      const searchQuery = this.searchFilter().trim();
      const searchable = this._basicFilter().searchable;

      if (typeof searchable === 'function') {
        this._searchSubject.next({ query: searchQuery, search: searchable });
      }
    });

    this._resolveSubject
      .pipe(
        distinctUntilChanged((a, b) => isEqual(a, b)),
        tap(() => this._isResolving.set(true)),
        switchMap(values => {
          const resolver = this._basicFilter().resolveSelectedOptions;

          if (typeof resolver !== 'function') {
            return of<readonly FilterOption[]>([]);
          }

          // On failure, fall back to raw values so the chips stay visible.
          return resolver(values).pipe(
            catchError(() =>
              of<readonly FilterOption[]>(
                values.map(value => ({
                  value,
                  label: verbatim(String(value)),
                })),
              ),
            ),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe(options => {
        this._isResolving.set(false);
        // Latest resolution first so a real label wins over a fallback one.
        this._resolvedSelectedOptions.update(previous =>
          uniqBy([...options, ...previous], option => option.value),
        );
      });

    effect(() => {
      const unresolvedValues = this._unresolvedSelectedValues();

      if (unresolvedValues.length) {
        this._resolveSubject.next(unresolvedValues);
      }
    });
  }

  public writeValue(selectedOptions: FilterValue): void {
    this._selectedOptions.set(selectedOptions);
    this._editMode.set(!hasSelection(selectedOptions));
  }

  public registerOnChange(fn: any): void {
    this._selectedOptionsChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  protected _switchEditMode(): void {
    switch (this._editMode()) {
      case true: {
        this._activeGroupIndex.set(null);
        this._editMode.set(!hasSelection(this._selectedOptions()));

        break;
      }
      case false: {
        this._editMode.set(true);
        // We need to wait for the next tick because we need the dropdown to be rendered before focusing it
        setTimeout(() => {
          this._openField();
        }, 0);

        break;
      }
    }
  }

  protected _openField(): void {
    if (this._isPeriodPickerFilter()) {
      this._periodPicker()?.open();

      return;
    }

    this._dropdown().focus();
  }

  protected _onOpen(): void {
    this._hasBeenOpened.set(true);
    this._basicFilter().onOpen?.();
  }

  protected _toggleGroup(groupIndex: number): void {
    this._activeGroupIndex.update(current =>
      current === groupIndex ? null : groupIndex,
    );
  }

  // Checked iff all the leaves it expands to are selected — derived from the
  // persisted selection so it stays coherent after a reload.
  protected _isActiveGroupOptionChecked(optionValue: FilterValue): boolean {
    const leaves = this._getLeavesForActiveGroupOption(optionValue);

    if (!leaves.length) {
      return false;
    }

    const selected = castArray(this._selectedOptions() ?? []);

    return leaves.every(leaf => selected.some(value => isEqual(value, leaf)));
  }

  protected _toggleActiveGroupOption(
    optionValue: FilterValue,
    checked: boolean,
  ): void {
    const leaves = this._getLeavesForActiveGroupOption(optionValue);

    if (!leaves.length) {
      return;
    }

    const current = castArray(this._selectedOptions() ?? []).filter(isNotNil);

    this._setSelectedOptions(
      checked
        ? uniq([...current, ...leaves])
        : current.filter(value => !leaves.some(leaf => isEqual(leaf, value))),
    );
  }

  // Leaf option values a group option expands to: the group's own `expand` when
  // provided, otherwise the base options declaring a dependency on it.
  private _getLeavesForActiveGroupOption(
    optionValue: FilterValue,
  ): FilterValueAtom[] {
    const group = this._activeGroup();

    if (!group) {
      return [];
    }

    if (group.expand) {
      return group.expand([optionValue]).flatMap(leaf => castArray(leaf));
    }

    const filter = this.filter();
    const options = (('options' in filter && filter.options) ||
      []) as FilterOption<Key>[];

    return options
      .filter(
        option =>
          FilterHelper.isFilterOption(option) &&
          !!option.dependencies &&
          castArray(option.dependencies).some(dep =>
            isEqual(dep.value, optionValue),
          ),
      )
      .flatMap(option => castArray(option.value));
  }

  protected _selectGroups(groupIndex: number, options: FilterValue): void {
    this._selectedGroupsByIndex.update(current => ({
      ...current,
      [groupIndex]: options,
    }));

    const groups = this._groups();

    const selectedOptions = uniq(
      Object.entries(this._selectedGroupsByIndex()).flatMap(
        ([index, groupOptions]) => {
          const group = groups[Number(index)];
          const groupValues = castArray(groupOptions ?? []).filter(isNotNil);

          if (!group || !groupValues.length) {
            return [];
          }

          return group.expand
            ? group.expand(groupValues)
            : this._expandByDependencies(groupValues);
        },
      ),
    ) as FilterValue;

    this._setSelectedOptions(selectedOptions);
  }

  // Default group expansion: select options declaring a dependency on a selected group value.
  private _expandByDependencies(groupValues: FilterValue[]): FilterValue[] {
    const filter = this.filter();
    const options = (('options' in filter && filter.options) ||
      []) as FilterOption<Key>[];

    return this.filteringOptions()
      .filter(option => {
        const fullOptionValue = options.find(
          o => FilterHelper.isFilterOption(o) && o.value === option.value,
        );

        return (
          !!fullOptionValue?.dependencies &&
          castArray(fullOptionValue.dependencies).some(dep =>
            groupValues.includes(dep.value),
          )
        );
      })
      .map(option => option.value);
  }

  protected _setSelectedOptions(selectedOptions: FilterValue | null): void {
    const consolidatedSelectedOptions =
      Array.isArray(selectedOptions) && selectedOptions.length === 0
        ? null
        : selectedOptions;

    const shouldResetComponent = isNil(consolidatedSelectedOptions);

    this._selectedOptionsChange(consolidatedSelectedOptions);
    this._selectedOptions.set(consolidatedSelectedOptions);

    if (shouldResetComponent) {
      this._editMode.set(true);

      setTimeout(() => {
        this._periodPicker()?.resetValue();
      });
    }
  }

  protected _onValueChange(value: FilterValue | null): void {
    this._setSelectedOptions(value);

    const filter = this.filter();

    if (filter?.onChange) {
      filter.onChange(value);
    }
  }

  /**
   * Date and switch have no tag state to collapse into, so they skip the edit-mode path.
   * `onChange` is left to the panel's control listener rather than fired twice.
   */
  protected _onAlwaysEditableValueChange(value: FilterValue): void {
    this._selectedOptions.set(value);
    this._selectedOptionsChange(value);
  }

  protected _isPeriodPickerFilter = computed(() => {
    return FilterHelper.isPeriodPickerFilter(this.filter());
  });

  protected _isDateFilter = computed(() => {
    return FilterHelper.isDateFilter(this.filter());
  });

  protected _isSwitchFilter = computed(() => {
    return FilterHelper.isSwitchFilter(this.filter());
  });

  protected _isTextFilter = computed(() => {
    return Boolean(this._basicFilter().searchable);
  });

  protected _selectedOptionsChange: (value: unknown) => void = _ => {};

  protected _onTouched = () => {};
}
